// @flow
import * as React from 'react';

import { useDispatch } from 'react-redux';

import { addCartProduct, getCart } from '@gandi/cart';
import { mailboxes } from '@gandi/redux-resource-price';
import { useTranslator } from '@gandi/translate';
import { Base, Button, Fqdn } from '@gandi/ui';

import { useMailboxPrice } from 'src/MailboxCreate/hooks';
import { fetchMailboxesPrices } from 'src/MailboxCreate/modules/actions';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { useMailboxOffers } from 'src/hooks/useMailboxOffers';

import type { MailboxOffer } from '../../../../../modules/mailbox/offers';
import type { Mailbox } from '../types';

const getMailboxOfferPayloadForCart = (
  offer: void | MailboxOffer,
): { capacity?: string, name?: string, size?: number, unit?: string } => ({
  capacity: offer?.capacity,
  name: offer?.name,
  size: offer?.size,
  unit: offer?.unit,
});

type Props = {
  mailbox: Mailbox,
};

export default function MailboxRenewSuggestionRow({
  mailbox,
}: Props): React.MixedElement {
  const { __, formatPrice, formatDate } = useTranslator();
  const dispatch = useDispatch();

  const isFetchingPrices = useAppSelector(state =>
    mailboxes.priceMailboxesResource.selectIsFetchingOne(
      state,
      'mailboxv2_create',
    ),
  );
  const isLoadedPrices = useAppSelector(state =>
    mailboxes.priceMailboxesResource.selectIsLoadedOne(
      state,
      'mailboxv2_create',
    ),
  );

  React.useEffect(() => {
    if (!isFetchingPrices && !isLoadedPrices) {
      dispatch(fetchMailboxesPrices());
    }
  }, [dispatch, isFetchingPrices, isLoadedPrices]);

  const { offers } = useMailboxOffers();
  const offer = offers?.find(o => o.mailbox_type === mailbox.mailbox_type);

  const { price, currency } = useMailboxPrice({
    mailboxType: mailbox.mailbox_type,
    minDuration: mailbox.last_renew_duration || 1,
  });

  const email = `${mailbox.login}@${mailbox.domain}`;

  const [adding, setAdding] = React.useState<boolean>(false);

  const addMailboxRenew = async () => {
    setAdding(true);

    try {
      await dispatch(
        addCartProduct({
          productData: {
            product_type: 'mailboxv2',
            process: 'renew',
            identifier: `${mailbox.domain}/slots/${mailbox.slot_id}`,
            payload: {
              ...getMailboxOfferPayloadForCart(offer),
              email,
              mailbox_type: mailbox.mailbox_type,
              // apiv3 compat
              mailboxv2_id: mailbox.slot_id,
              duration: mailbox.last_renew_duration,
              fake_renew: mailbox.fake_renew,
            },
          },
          options: {
            headers: { 'X-Dry-Run': 1, 'X-Orga-Reset': 0 },
          },
        }),
      );
    } catch (err) {
      // fail silently
      setAdding(false);
    }
    dispatch(getCart());
  };

  return (
    <Base
      flex
      columnGap="small"
      rowGap="xxsmall"
      marginBottom="xsmall"
      flexAlignItemsStart
      flexWrap
    >
      <Base>
        <Fqdn color="base">{email}</Fqdn>
        <Base size="small" color="light">
          {__('Expires on %(dateEnd)s.', {
            dateEnd: formatDate(mailbox.expires_at, 'LL'),
          })}
        </Base>
      </Base>
      {price && (
        <Button
          type="button"
          kind="link"
          onClick={addMailboxRenew}
          loading={adding}
        >
          {__('Renew for %(period)s at %(price)s monthly', {
            period:
              mailbox.last_renew_duration === 1
                ? __('%(count)s months', { count: 1 })
                : __('%(count)s years', { count: 1 }),
            price: formatPrice(price.priceBeforeTaxes, currency),
          })}
        </Button>
      )}
    </Base>
  );
}
